import React from 'react';
import { Shield, Lock, CreditCard, MessageCircle } from 'lucide-react';

const features = [
  {
    icon: Shield,
    title: 'Segurança Garantida',
    description: 'Protocolos de segurança avançados',
    color: 'from-blue-500/20 to-cyan-500/20',
    iconColor: 'text-blue-500',
    bgIcon: 'bg-blue-500/10'
  },
  {
    icon: Lock,
    title: 'Dados Protegidos',
    description: 'Criptografia de ponta a ponta',
    color: 'from-green-500/20 to-emerald-500/20',
    iconColor: 'text-green-500',
    bgIcon: 'bg-green-500/10'
  },
  {
    icon: CreditCard,
    title: 'Pagamento Seguro',
    description: 'Transações certificadas',
    color: 'from-purple-500/20 to-violet-500/20',
    iconColor: 'text-purple-500',
    bgIcon: 'bg-purple-500/10'
  },
  {
    icon: MessageCircle,
    title: 'Suporte 24/7',
    description: 'Atendimento sempre disponível',
    color: 'from-orange-500/20 to-amber-500/20',
    iconColor: 'text-orange-500',
    bgIcon: 'bg-orange-500/10'
  }
];

export function TrustBanner() {
  return (
    <section className="relative py-16 lg:py-20 overflow-hidden">
      {/* Modern Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-muted/40 via-background/80 to-muted/30"></div>
      
      {/* Animated Mesh Background */}
      <div className="absolute inset-0 opacity-30">
        <div className="mesh-gradient"></div>
      </div>
      
      {/* Floating Decorative Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-br from-orange-primary/[0.03] to-transparent rounded-full blur-3xl animate-float-slow"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-orange-primary/[0.02] to-transparent rounded-full blur-3xl animate-float-slow" style={{ animationDelay: '2s' }}></div>
      
      {/* Wave Effect */}
      <div className="absolute inset-0 opacity-20">
        <div className="wave-lines">
          <div className="wave-line wave-line-1"></div>
          <div className="wave-line wave-line-2"></div>
        </div>
      </div>
      
      <div className="container-modern relative z-10">
        {/* Enhanced Section Header */}
        <div className="text-center mb-12 lg:mb-16">
          {/* Compact Elegant Badge */}
          <div className="inline-flex items-center justify-center mb-6 lg:mb-8">
            <div className="glass-modern-enhanced px-3 py-2 sm:px-4 sm:py-2.5 lg:px-5 lg:py-3 rounded-xl lg:rounded-2xl border shadow-medium">
              <div className="flex items-center space-x-2 lg:space-x-2.5">
                <div className="w-1.5 h-1.5 lg:w-2 lg:h-2 bg-gradient-orange-elegant rounded-full animate-pulse"></div>
                <span className="text-xs sm:text-sm lg:text-sm font-medium gradient-text-orange whitespace-nowrap">
                  Sua confiança é nossa prioridade
                </span>
                <div className="w-1.5 h-1.5 lg:w-2 lg:h-2 bg-gradient-orange-elegant rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
              </div>
            </div>
          </div>
          
          {/* Main Title */}
          <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-4 gradient-text-orange">
            Tecnologia de Confiança
          </h2>
          
          {/* Subtitle */}
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Milhares de eventos protegidos por nossa infraestrutura de segurança
          </p>
        </div>

        {/* Horizontal Features Layout - Always in one row */}
        <div className="trust-features-horizontal">
          {features.map((feature, index) => (
            <div
              key={index}
              className="trust-feature-card group"
              style={{
                animationDelay: `${index * 150}ms`
              }}
            >
              {/* Card Container with Advanced Effects */}
              <div className="relative h-full">
                {/* Gradient Glow Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500 blur-lg`}></div>
                
                {/* Main Card */}
                <div className="relative glass-modern-enhanced p-4 sm:p-6 lg:p-8 rounded-2xl border hover:border-orange-primary/30 transition-all duration-500 hover:-translate-y-2 hover:shadow-elegant h-full">
                  {/* Icon Container */}
                  <div className="flex items-center justify-center mb-4 sm:mb-6">
                    <div className={`relative w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 ${feature.bgIcon} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-all duration-300`}>
                      {/* Icon Glow Effect - Hide on Mobile */}
                      <div className={`trust-decorative-mobile-hidden absolute inset-0 bg-gradient-to-br ${feature.color} rounded-2xl opacity-0 group-hover:opacity-70 transition-opacity duration-300`}></div>
                      
                      {/* Icon */}
                      <feature.icon 
                        size={20} 
                        className={`${feature.iconColor} relative z-10 transition-all duration-300 group-hover:scale-110 sm:w-6 sm:h-6 lg:w-6 lg:h-6`} 
                      />
                      
                      {/* Animated Ring - Hide on Mobile */}
                      <div className="trust-decorative-mobile-hidden absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-current opacity-0 group-hover:opacity-30 transition-all duration-300 animate-ping"></div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="text-center space-y-2 sm:space-y-3">
                    <h4 className="font-heading font-semibold text-sm sm:text-base lg:text-lg group-hover:gradient-text-orange transition-all duration-300 leading-tight">
                      {feature.title}
                    </h4>
                    <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                  
                  {/* Decorative Bottom Border - Hide on Mobile */}
                  <div className="trust-decorative-mobile-hidden absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-orange-elegant rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300"></div>
                </div>
                
                {/* Floating Particles Effect - Hide on Mobile */}
                <div className="trust-decorative-mobile-hidden absolute -top-2 -right-2 w-4 h-4 bg-gradient-orange-elegant rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 animate-bounce"></div>
                <div className="trust-decorative-mobile-hidden absolute -bottom-2 -left-2 w-3 h-3 bg-gradient-orange-elegant rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}